import {
  ACTIVATE_SERVICE_WORKER_REQUEST,
  DEFAULT_ACTIVATION_ACK_TIMEOUT_MS,
  SERVICE_WORKER_UPDATE_PROTOCOL_VERSION,
  isActivateServiceWorkerResponse,
  isServiceWorkerUpdateRequestId,
} from "./service-worker-update-model.ts";

export type ServiceWorkerActivationProtocolCode =
  | "ACTIVATION_POST_FAILED"
  | "ACTIVATION_REJECTED"
  | "ACTIVATION_ACK_TIMEOUT"
  | "ACTIVATION_CANCELLED";

export type ServiceWorkerActivationProtocolResult =
  | {
      readonly status: "accepted";
      readonly requestId: string;
    }
  | {
      readonly status: "failed";
      readonly requestId: string;
      readonly code: ServiceWorkerActivationProtocolCode;
    };

export interface ServiceWorkerActivationProtocolOptions {
  readonly requestId?: string;
  readonly timeoutMs?: number;
  readonly signal?: AbortSignal;
  readonly createMessageChannel?: () => MessageChannel;
  readonly scheduleTimeout?: (
    callback: () => void,
    timeoutMs: number,
  ) => ReturnType<typeof setTimeout>;
  readonly cancelTimeout?: (timeoutId: ReturnType<typeof setTimeout>) => void;
}

let fallbackRequestSequence = 0;

export function createServiceWorkerUpdateRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // A deterministic safe fallback remains available in constrained browsers.
  }

  fallbackRequestSequence = (fallbackRequestSequence + 1) % 1_000_000;
  return `update-${Date.now().toString(36)}-${fallbackRequestSequence.toString(36)}`;
}

function normalizeTimeout(timeoutMs: number | undefined): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)) {
    return DEFAULT_ACTIVATION_ACK_TIMEOUT_MS;
  }

  return Math.max(1, Math.min(timeoutMs, DEFAULT_ACTIVATION_ACK_TIMEOUT_MS));
}

function createBrowserMessageChannel(): MessageChannel {
  return new MessageChannel();
}

export function requestServiceWorkerActivation(
  worker: ServiceWorker,
  options: ServiceWorkerActivationProtocolOptions = {},
): Promise<ServiceWorkerActivationProtocolResult> {
  const requestId = options.requestId ?? createServiceWorkerUpdateRequestId();
  if (!isServiceWorkerUpdateRequestId(requestId)) {
    throw new Error("Service-worker update request IDs must be safe.");
  }

  if (options.signal?.aborted) {
    return Promise.resolve({
      status: "failed",
      requestId,
      code: "ACTIVATION_CANCELLED",
    });
  }

  const channel = (
    options.createMessageChannel ?? createBrowserMessageChannel
  )();
  const scheduleTimeout = options.scheduleTimeout ?? setTimeout;
  const cancelTimeout = options.cancelTimeout ?? clearTimeout;
  const timeoutMs = normalizeTimeout(options.timeoutMs);

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer !== null) {
        try {
          cancelTimeout(timer);
        } catch {
          // Cleanup errors cannot change a completed protocol result.
        }
        timer = null;
      }
      channel.port1.removeEventListener("message", handleMessage);
      options.signal?.removeEventListener("abort", handleAbort);
      try {
        channel.port1.close();
        channel.port2.close();
      } catch {
        // Closing an already-disconnected port is harmless.
      }
    };

    const finish = (result: ServiceWorkerActivationProtocolResult) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(result);
    };

    function handleMessage(event: MessageEvent<unknown>) {
      if (
        !isActivateServiceWorkerResponse(event.data) ||
        event.data.requestId !== requestId
      ) {
        return;
      }

      finish(
        event.data.ok
          ? { status: "accepted", requestId }
          : {
              status: "failed",
              requestId,
              code: "ACTIVATION_REJECTED",
            },
      );
    }

    function handleAbort() {
      finish({
        status: "failed",
        requestId,
        code: "ACTIVATION_CANCELLED",
      });
    }

    channel.port1.addEventListener("message", handleMessage);
    channel.port1.start();
    options.signal?.addEventListener("abort", handleAbort, { once: true });

    timer = scheduleTimeout(() => {
      finish({
        status: "failed",
        requestId,
        code: "ACTIVATION_ACK_TIMEOUT",
      });
    }, timeoutMs);

    try {
      worker.postMessage(
        {
          type: ACTIVATE_SERVICE_WORKER_REQUEST,
          version: SERVICE_WORKER_UPDATE_PROTOCOL_VERSION,
          requestId,
        },
        [channel.port2],
      );
    } catch {
      finish({
        status: "failed",
        requestId,
        code: "ACTIVATION_POST_FAILED",
      });
    }
  });
}
